import { enhancedApi } from '@store/generated/encounters';
import type { RootState } from '@store/index';

import { ENCOUNTER_CACHE_TTL, ENCOUNTER_TAG_TYPES } from '../constants';

import { selectActiveWorkflowEncounterId } from './selectors';
import { clearActiveWorkflowEncounter, setActiveWorkflowEncounter } from './workflowSlice';

/**
 * Encounters API - extends the generated OpenAPI client
 *
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization with:
 * - Cache tags for automatic invalidation
 * - Optimistic updates for better UX
 * - Integration with workflow state
 * - Custom V1 endpoints not yet in OpenAPI spec
 * - Bidirectional cache invalidation with appointments feature
 */
export const encountersApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: [
      ...ENCOUNTER_TAG_TYPES,
      // Cross-feature tags for bidirectional cache invalidation
      // These allow encounter mutations to invalidate appointment caches
      'NextAppointment',
      'AppointmentsList',
      // Notes cache invalidation when transcripts are deleted
      'NoteList',
    ],
    endpoints: {
      // Enhance: Get single encounter
      getV2ProviderEncountersByEncounterId: {
        providesTags: (_result: any, _error: any, { encounterId }: any) => [
          { type: 'Encounter' as const, id: encounterId },
        ],
        keepUnusedDataFor: ENCOUNTER_CACHE_TTL,
      },

      // Enhance: List encounters
      getV2ProviderEncounters: {
        providesTags: (result: any) =>
          result
            ? [
                ...result.items.map(({ encounter_id }: any) => ({
                  type: 'Encounter' as const,
                  id: encounter_id,
                })),
                { type: 'EncounterList' as const, id: 'LIST' },
              ]
            : [{ type: 'EncounterList', id: 'LIST' }],
        keepUnusedDataFor: ENCOUNTER_CACHE_TTL,
      },

      // Enhance: Create encounter
      postV2ProviderEncounters: {
        invalidatesTags: [
          { type: 'EncounterList', id: 'LIST' },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is created, appointment caches must be cleared
          // to reflect that the appointment now has an associated encounter
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
        // Phase 2: Automatically set as active workflow after creation
        onQueryStarted: async (_arg: any, { dispatch, queryFulfilled }: any) => {
          try {
            const { data: newEncounter } = await queryFulfilled;
            dispatch(
              setActiveWorkflowEncounter({
                encounterId: newEncounter.encounter_id,
                reason: 'created',
              }),
            );
          } catch (error) {
            // Error already handled by mutation state
          }
        },
      },

      // Enhance: Update encounter
      patchV2ProviderEncountersByEncounterId: {
        invalidatesTags: (_result: any, _error: any, { encounterId }: any) => [
          { type: 'Encounter', id: encounterId },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is updated (patient changed, template changed, etc),
          // appointment caches must be cleared to reflect the updated state
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
        // Optimistic update for better UX
        // Note: This only applies encounterUpdate (request body) to cache.
        // When includeDefaults=true, the response will contain computed_defaults,
        // which the caller handles separately (not part of optimistic update).
        onQueryStarted: async (
          { encounterId, encounterUpdate }: any,
          { dispatch, queryFulfilled }: any,
        ) => {
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterId',
              { encounterId },
              (draft: any) => {
                Object.assign(draft, encounterUpdate);
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },

      // Enhance: Re-link the encounter to a different existing appointment
      // (GDN-3909). This moves a LINK, never a time: the backend must not
      // touch either appointment's schedule, so nothing here may optimistically
      // edit start_time. Both appointment caches are invalidated because the
      // old slot becomes unlinked and the new one becomes linked, which is
      // exactly what collision detection keys on.
      patchV2ProviderEncountersByEncounterIdAppointment: {
        invalidatesTags: (
          _result: unknown,
          _error: unknown,
          { encounterId }: { encounterId: string },
        ) => [
          { type: 'Encounter' as const, id: encounterId },
          { type: 'EncounterList' as const, id: 'LIST' },
          { type: 'NextAppointment' as const, id: 'NEXT' },
          { type: 'AppointmentsList' as const, id: 'LIST' },
        ],
      },

      // Enhance: Delete encounter
      deleteV2ProviderEncountersByEncounterId: {
        invalidatesTags: [
          // NOTE: Do NOT invalidate specific encounter tag on delete
          // Invalidating { type: 'Encounter', id: encounterId } causes RTK Query to refetch
          // the deleted encounter (and its documents), resulting in 404 errors.
          // Instead, only invalidate lists so they update to reflect the deletion.
          { type: 'EncounterList', id: 'LIST' },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is deleted, appointment caches must be cleared
          // to reflect that the appointment is now available again (no encounter)
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
        // Phase 2: Clear workflow state if deleting active encounter
        onQueryStarted: async (
          { encounterId }: any,
          { dispatch, getState, queryFulfilled }: any,
        ) => {
          try {
            await queryFulfilled;
            // Check if deleting active workflow encounter
            const state = getState() as RootState;
            const activeId = selectActiveWorkflowEncounterId(state);
            if (activeId === encounterId) {
              dispatch(clearActiveWorkflowEncounter({ reason: 'encounter-deleted' }));
            }
          } catch (error) {
            // Error handled by mutation state
          }
        },
      },

      // Enhance: Get single document
      getV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
        providesTags: (_result: any, _error: any, { encounterId, documentId }: any) => [
          { type: 'EncounterDocument' as const, id: documentId },
          // { type: 'Encounter' as const, id: encounterId },
        ],
        keepUnusedDataFor: ENCOUNTER_CACHE_TTL,
      },

      // Enhance: Create document
      postV2ProviderEncountersByEncounterIdDocuments: {
        invalidatesTags: [
          // NOTE: Don't invalidate Encounter here - calling code (EncounterDocuments,
          // EncounterRecordingWidget) always calls saveNow() immediately after which
          // invalidates and refetches the encounter. This prevents double refetches.
        ],
        // Optimistic update for better UX - immediately show new document in encounter.inputs
        onQueryStarted: async (
          { encounterId, createDocumentRequest }: any,
          { dispatch, queryFulfilled }: any,
        ) => {
          // Use transcription_session_id as a deterministic temp ID to prevent duplicates
          // when onQueryStarted is triggered multiple times (e.g., via redux-state-sync)
          const tempId = createDocumentRequest.transcription_session_id
            ? `temp-${createDocumentRequest.transcription_session_id}`
            : `temp-${Date.now()}`;

          // Optimistically add to encounter.inputs
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterId',
              { encounterId },
              (draft: any) => {
                if (!draft.inputs) {
                  draft.inputs = [];
                }

                // Check if temp entry with this ID already exists (prevents duplicates from double dispatch)
                const existingTempIndex = draft.inputs.findIndex(
                  (input: any) => input.encounter_document_id === tempId,
                );
                if (existingTempIndex !== -1) {
                  return;
                }

                // Add temporary document reference that will be replaced by server response
                draft.inputs.push({
                  encounter_document_id: tempId,
                  type: createDocumentRequest.type,
                  title: createDocumentRequest.title || null,
                  created_at: new Date().toISOString(),
                  status: 'pending' as const,
                });
              },
            ),
          );

          try {
            const { data: createdDocument } = await queryFulfilled;
            // Update the temporary entry with the real document ID from server
            dispatch(
              encountersApi.util.updateQueryData(
                'getV2ProviderEncountersByEncounterId',
                { encounterId },
                (draft: any) => {
                  if (draft.inputs) {
                    // Find the specific temp entry we created
                    const tempIndex = draft.inputs.findIndex(
                      (input: any) => input.encounter_document_id === tempId,
                    );
                    if (tempIndex !== -1 && createdDocument) {
                      // Check if real document already exists (another dispatch may have replaced it)
                      const existingRealIndex = draft.inputs.findIndex(
                        (input: any) =>
                          input.encounter_document_id === createdDocument.encounter_document_id,
                      );
                      if (existingRealIndex !== -1 && existingRealIndex !== tempIndex) {
                        // Real document already exists, just remove the temp entry
                        draft.inputs.splice(tempIndex, 1);
                      } else {
                        // Replace temp with real document
                        draft.inputs[tempIndex] = {
                          encounter_document_id: createdDocument.encounter_document_id,
                          type: createdDocument.type,
                          title: createdDocument.title || null,
                          created_at: createdDocument.created_at || new Date().toISOString(),
                          status: createdDocument.status || 'pending',
                        };
                      }
                    }
                  }
                },
              ),
            );
          } catch {
            // On error, undo the optimistic update
            patchResult.undo();
          }
        },
      },

      // Enhance: Update document
      patchV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
        // Only invalidate the document itself - encounter's inputs array (references only) doesn't change
        invalidatesTags: (_result: any, _error: any, { documentId }: any) => [
          { type: 'EncounterDocument', id: documentId },
        ],
        // Optimistic update for better UX
        onQueryStarted: async (
          { encounterId, documentId, documentUpdate }: any,
          { dispatch, queryFulfilled }: any,
        ) => {
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterIdDocumentsAndDocumentId',
              { encounterId, documentId },
              (draft: any) => {
                Object.assign(draft, documentUpdate);
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },

      // Enhance: Delete document
      deleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
        invalidatesTags: (_result, _error, { documentId }) => [
          { type: 'EncounterDocument', id: documentId },
          // NOTE: Don't invalidate Encounter here - calling code (EncounterDocuments)
          // calls saveNow() immediately after which invalidates and re-fetches the
          // encounter. This prevents double re-fetches.
        ],
      },

      // Enhance: PUT working billing claim (full-replace semantics)
      // NOTE: No invalidatesTags here — onQueryStarted already replaces the
      // entire encounter cache with the server response, making a refetch redundant.
      putV2ProviderEncountersByEncounterIdWorkingBillingClaim: {
        onQueryStarted: async (
          { encounterId, workingBillingClaimPutRequest }: any,
          { dispatch, queryFulfilled }: any,
        ) => {
          // Optimistic update: patch working_billing_claim in cache
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterId',
              { encounterId },
              (draft: any) => {
                if (!draft.working_billing_claim) {
                  draft.working_billing_claim = {};
                }
                const claim = draft.working_billing_claim;

                if (workingBillingClaimPutRequest.line_items !== undefined) {
                  claim.line_items = workingBillingClaimPutRequest.line_items;
                }
                if (workingBillingClaimPutRequest.diagnosis_codes !== undefined) {
                  claim.diagnosis_codes = workingBillingClaimPutRequest.diagnosis_codes;
                }
                if (workingBillingClaimPutRequest.diagnosis_code_sources !== undefined) {
                  claim.diagnosis_code_sources = workingBillingClaimPutRequest.diagnosis_code_sources;
                }
                if (workingBillingClaimPutRequest.place_of_service !== undefined) {
                  claim.place_of_service = workingBillingClaimPutRequest.place_of_service;
                }
              },
            ),
          );

          try {
            const { data: updatedEncounter } = await queryFulfilled;
            // Replace entire encounter cache with server response for consistency
            dispatch(
              encountersApi.util.updateQueryData(
                'getV2ProviderEncountersByEncounterId',
                { encounterId },
                (draft: any) => {
                  Object.assign(draft, updatedEncounter);
                },
              ),
            );
          } catch {
            patchResult.undo();
          }
        },
      },

      // Enhance: Delete all transcripts
      // Permanently deletes transcription sessions, clears note transcript arrays
      deleteV2ProviderEncountersByEncounterIdTranscripts: {
        invalidatesTags: (_result, _error, { encounterId }) => [
          { type: 'Encounter', id: encounterId },
          { type: 'EncounterList', id: 'LIST' },
          // Note's transcript array is cleared to [], can_regenerate becomes false
          { type: 'NoteList', id: 'LIST' },
        ],
        // Optimistically set transcripts_deleted: true for immediate UI feedback
        onQueryStarted: async (
          { encounterId }: { encounterId: string },
          { dispatch, queryFulfilled }: any,
        ) => {
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterId',
              { encounterId },
              (draft: any) => {
                draft.transcripts_deleted = true;
                draft.can_delete_transcripts = false;
                // Clear inputs (dictations) optimistically
                draft.inputs = [];
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Custom V1 endpoint: Submit encounter for processing
      // Response includes optional thread_id for v2 streaming flow
      // Optional eventId is forwarded as `event_id` so the backend Meta CAPI
      // Purchase fire shares an ID with the browser pixel for dedup (gdn-1417).
      submitEncounter: builder.mutation<
        { note_id: string; encounter_id: string; thread_id?: string },
        { encounterId: string; eventId?: string }
      >({
        query: ({ encounterId, eventId }) => ({
          url: '/encounter/startEncounterProcessing',
          method: 'POST',
          body: {
            encounter_id: encounterId,
            ...(eventId ? { event_id: eventId } : {}),
          },
        }),
        // Invalidate encounter cache (status changes to 'processing')
        invalidatesTags: (_result, _error, { encounterId }) => [
          { type: 'Encounter', id: encounterId },
          { type: 'EncounterList', id: 'LIST' },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is submitted, status changes to 'processing' which affects
          // appointment collision logic (processing encounters are treated as "has encounter")
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
      }),

      // V2 endpoint: Get processing status for note generation workflow
      // Used to hydrate streaming state when joining mid-stream
      getProcessingStatus: builder.query<
        {
          thread_id: string;
          status: 'pending' | 'processing' | 'completed' | 'interrupted' | 'error';
          workflow_name: string;
          current_step: string | null;
          current_step_display_name: string | null;
          completed_steps: string[];
          total_steps: number;
          steps: Array<{ id: string; display_name: string; order: number }>;
          note_id?: string;
        },
        { threadId: string }
      >({
        query: ({ threadId }) => ({
          url: `/v2/provider/note-processing-workflows/status/${threadId}`,
          method: 'GET',
        }),
      }),
    }),
    overrideExisting: false,
  });

/**
 * Export ONLY user-friendly hook names
 *
 * ⚠️ NEVER export verbose OpenAPI-generated names like:
 * - useGetV2ProviderEncountersByEncounterIdQuery
 *
 * ✅ ALWAYS export user-friendly names like:
 * - useGetEncounterQuery
 */
export const {
  // Query hooks
  useGetV2ProviderEncountersByEncounterIdQuery: useGetEncounterQuery,
  useGetV2ProviderEncountersQuery: useListEncountersQuery,
  useLazyGetV2ProviderEncountersQuery: useLazyListEncountersQuery,
  useLazyGetV2ProviderEncountersByEncounterIdQuery: useLazyGetEncounterQuery,

  // Mutation hooks
  usePostV2ProviderEncountersMutation: useCreateEncounterMutation,
  usePatchV2ProviderEncountersByEncounterIdMutation: useUpdateEncounterMutation,
  // GDN-3909: rebind an encounter to an existing appointment (no time moves).
  usePatchV2ProviderEncountersByEncounterIdAppointmentMutation:
    useLinkEncounterAppointmentMutation,
  useDeleteV2ProviderEncountersByEncounterIdMutation: useDeleteEncounterMutation,
  usePostV2ProviderEncountersFromAppointmentByAppointmentIdMutation:
    useCreateEncounterFromAppointmentMutation,
  usePostV2ProviderEncountersCreateTranscriptOnEncounterFromExampleMutation:
    useCreateTranscriptFromExampleMutation,
  useDeleteV2ProviderEncountersByEncounterIdTranscriptsMutation: useDeleteTranscriptsMutation,
  useSubmitEncounterMutation, // Custom V1 endpoint

  // Document-related queries
  useGetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdQuery: useGetDocumentQuery,
  useLazyGetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdQuery: useLazyGetDocumentQuery,

  // Document-related mutations
  usePostV2ProviderEncountersByEncounterIdDocumentsMutation: useCreateDocumentMutation,
  useDeleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdMutation:
    useDeleteDocumentMutation,
  usePatchV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdMutation:
    useUpdateDocumentMutation,

  // Attestation defaults
  useGetV2ProviderEncountersDefaultsQuery: useGetAttestationDefaultsQuery,
  useLazyGetV2ProviderEncountersDefaultsQuery: useLazyGetAttestationDefaultsQuery,

  // Working billing claim
  usePutV2ProviderEncountersByEncounterIdWorkingBillingClaimMutation:
    useUpdateWorkingBillingClaimMutation,

  // Processing status (V2 streaming)
  useGetProcessingStatusQuery,
  useLazyGetProcessingStatusQuery,
} = encountersApi;
